import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import c from 'classnames';
import s from './common.scss';
const propDefaults = {};
/**
 * Each Facet implementation requires the FacetContent component. Separating out the props in each implementation
 * will get unwieldy and is prone to a lot of accidental errors or annoying component differences. So this variable keeps
 * of all the FacetContent props that we pass through from each Facet implementation as is.
 */ const passThroughContentProps = [
    'size',
    'children'
];
/**
 * These are present in the user-facing API, but aren't used in disparate Facet implementations. They're mostly used
 * in shared Facet code. Like when we fetch the initial root component `className` or in its FacetContent component.
 */ const commonPropsToStrip = [
    'size',
    'children',
    'selected'
];
/**
 * Fetches all the {@link FacetContentProps} out of the main props object so that it's easy to pass them along to the
 * FacetContent component.
 */ function getContentProps(propsWithoutDefaults) {
    const props = _object_spread({}, propDefaults, propsWithoutDefaults);
    return pick(props, passThroughContentProps);
}
function getRootClassNames({ selected, className }) {
    return c(s['root'], selected && s['selected'], className);
}
/**
 * Omits all {@link FacetContentProps} from the main props object. Useful in cases where we pass the `{...rest}` to the
 * component wrapping our FacetContent.
 *
 * e.g.
 * ```
 *  [...]
 *  const radioProps = getRootProps(props)
 *  return (
 *    <RadioButton {...radioProps}>
 *      <FacetContent {...contentProps}/>
 *    </RadioButton>
 *  )
 * ```
 */ function getRootProps(propsWithoutDefaults) {
    const props = _object_spread({}, propDefaults, propsWithoutDefaults);
    return _object_spread_props(_object_spread({}, omit(props, [
        // These are only relevant to common Facet code, like the `getRootClassNames` function.
        // Removing them keeps us from accidentally passing an invalid prop to a DOM element
        ...commonPropsToStrip
    ])), {
        className: getRootClassNames(props)
    });
}
/**
 * Takes a props type, a props object, and returns a set of variables for easy use inside any Facet implementation.
 */ export function useFacet(props) {
    return {
        /**
     * Props that -- in most cases -- should be spread onto the root component of your Facet implementation.
     */ rootProps: getRootProps(props),
        /**
     * Props that should be spread onto the content component of your Facet implementation.
     */ contentProps: getContentProps(props)
    };
}
